import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api.js";
import type { PacingResult, PacingScene } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";

interface Props {
  projectId: string;
  onOpenScene: (sceneId: string) => void;
}

type Dim = "tension" | "pace" | "dialoguePercent" | "words";
const DIMS: { key: Dim; label: string; max: (s: PacingScene[]) => number }[] = [
  { key: "tension", label: "Tension", max: () => 10 },
  { key: "pace", label: "Pace", max: () => 10 },
  { key: "dialoguePercent", label: "Dialogue %", max: () => 100 },
  { key: "words", label: "Scene length", max: (s) => Math.max(1, ...s.map((x) => x.words)) }
];

/**
 * Pacing / Tension map — a deterministic story-level view of how a chosen
 * dimension rises and falls across scenes, with chapter markers. Values are
 * analysis (tension uses the author's scene value when set, else a heuristic),
 * not objective truth. Click a point to open the scene.
 */
export function PacingPanel({ projectId, onOpenScene }: Props) {
  const toast = useToast();
  const [data, setData] = useState<PacingResult | null>(null);
  const [dim, setDim] = useState<Dim>("tension");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.pacing(projectId)); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't load pacing"); }
    finally { setLoading(false); }
  }, [projectId, toast]);
  useEffect(() => { void load(); }, [load]);

  const dimSpec = DIMS.find((d) => d.key === dim)!;
  const chart = useMemo(() => {
    if (!data || data.scenes.length === 0) return null;
    const W = 760, H = 240, padL = 34, padR = 12, padT = 14, padB = 28;
    const n = data.scenes.length;
    const max = dimSpec.max(data.scenes);
    const x = (i: number) => padL + (n <= 1 ? 0 : (i / (n - 1)) * (W - padL - padR));
    const y = (v: number) => padT + (1 - Math.min(1, v / max)) * (H - padT - padB);
    const pts = data.scenes.map((s, i) => ({ s, i, cx: x(i), cy: y(Number(s[dim])) }));
    const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.cx.toFixed(1)},${p.cy.toFixed(1)}`).join(" ");
    const first = pts[0]!;
    const last = pts[pts.length - 1]!;
    const area = `${path} L${last.cx.toFixed(1)},${(H - padB).toFixed(1)} L${first.cx.toFixed(1)},${(H - padB).toFixed(1)} Z`;
    const chapterTicks = data.chapters.map((c) => ({ title: c.title, cx: x(c.firstOrder) }));
    return { W, H, padB, pts, path, area, chapterTicks, max };
  }, [data, dim, dimSpec]);

  if (loading) return <div className="insights-loading"><Icon name="timeline" size={22} className="spin" style={{ color: "var(--accent)" }} /></div>;
  if (!data) return null;

  return (
    <div className="intel-scroll">
      <section className="card insight-card">
        <div className="insight-card-head">
          <Icon name="timeline" size={16} style={{ color: "var(--accent)" }} />
          <h3>Pacing &amp; Tension</h3>
          <span className="spacer" />
          <div className="row gap-2">
            {DIMS.map((d) => (
              <button key={d.key} className={`btn btn-sm ${dim === d.key ? "btn-primary" : "btn-ghost"}`} onClick={() => setDim(d.key)}>{d.label}</button>
            ))}
          </div>
        </div>
        <p className="faint" style={{ fontSize: 12, margin: "0 0 10px" }}>
          Analysis across your scenes (not objective truth). Tension uses your scene value when set, otherwise a heuristic you can override in the Scene inspector.
        </p>
        {!chart ? (
          <p className="muted insight-empty">No scenes to chart yet.</p>
        ) : (
          <svg className="pacing-svg" viewBox={`0 0 ${chart.W} ${chart.H}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label={`${dimSpec.label} across scenes`}>
            {[0, 0.25, 0.5, 0.75, 1].map((f) => {
              const yy = 14 + (1 - f) * (chart.H - 14 - chart.padB);
              return <g key={f}>
                <line x1={34} y1={yy} x2={chart.W - 12} y2={yy} stroke="var(--border-soft)" strokeWidth={1} />
                <text x={28} y={yy + 3} textAnchor="end" fontSize={9} fill="var(--text-faint)">{Math.round(chart.max * f)}</text>
              </g>;
            })}
            {chart.chapterTicks.map((c, i) => (
              <line key={i} x1={c.cx} y1={14} x2={c.cx} y2={chart.H - chart.padB} stroke="var(--border-strong)" strokeWidth={1} strokeDasharray="2 3" opacity={0.5} />
            ))}
            <path d={chart.area} fill="var(--accent-glow)" opacity={0.5} />
            <path d={chart.path} fill="none" stroke="var(--accent)" strokeWidth={2} strokeLinejoin="round" />
            {chart.pts.map((p) => (
              <circle key={p.s.sceneId} cx={p.cx} cy={p.cy} r={3.5} fill="var(--accent)" stroke="var(--bg-0)" strokeWidth={1}
                className="pacing-dot" onClick={() => onOpenScene(p.s.sceneId)}>
                <title>{p.s.chapterTitle} / {p.s.title}: {dimSpec.label} {Number(p.s[dim])}{p.s.tensionAuthored && dim === "tension" ? " (yours)" : ""}</title>
              </circle>
            ))}
          </svg>
        )}
        <div className="faint" style={{ fontSize: 11, marginTop: 4 }}>{data.scenes.length} scenes · dashed lines mark chapters · click a point to open the scene.</div>
      </section>
    </div>
  );
}
